import { useState } from 'react'
import { Loader2, Radio } from 'lucide-react'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, Note } from '@/shared/ui/states'
import { createCoverageEvent } from '@/features/coverage/api'
import { BroadcastPanel } from '@/features/coverage/BroadcastPanel'
import type { CoverageEventResponse } from '@/shared/api/types'
import { addEvidence } from './api'

/**
 * Live video from the scene — by reusing `coverage`, not by rebuilding it.
 *
 * ## What this component does NOT do
 *
 * It does not open an `RTCPeerConnection`, mint a token, or talk to LiveKit. `coverage`
 * already does all of that: a room per event, publish grants inside a server-minted token,
 * watch time from webhooks, recordings to object storage via egress. A second SFU
 * integration here would be a second set of the same bugs, and the two would disagree
 * about which URL the browser connects to within a release.
 *
 * That last point is the one that costs an afternoon: the backend reaches LiveKit on the
 * compose network and the browser reaches it on the published port — in production a
 * public `wss://`. The token response carries the browser's `wsUrl`, and
 * `coverage/useBroadcast` is what reads it. Nothing here needs to know either address.
 *
 * ## The three steps
 *
 * 1. `POST /coverage/events` with `subjectTypeCode: "INCIDENT"` and `subjectRef` — which
 *    `IncidentCoverageSubjectResolver` resolves server-side, so the event carries the
 *    incident's ref and title rather than a bare uuid. `openMainCamera` creates the camera
 *    slot in the SAME transaction; posting it separately is how PollSphere ends up
 *    reporting success on an event with no camera.
 * 2. `<BroadcastPanel>` — the existing one — puts the device on air.
 * 3. `POST /incidents/{id}/evidence` with `kind: "LIVE_FEED"` and the coverage event id,
 *    so the feed appears in the incident's evidence list like everything else.
 */
export function LiveFeedPanel({
  incidentId, onAttached,
}: {
  incidentId: string
  onAttached?: () => void | Promise<void>
}) {
  const [event, setEvent] = useState<CoverageEventResponse | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [attached, setAttached] = useState(false)

  const open = async () => {
    setBusy(true)
    setError(null)
    try {
      const created = await createCoverageEvent({
        title: `Live from incident ${incidentId.slice(0, 8)}`,
        description: 'Opened from the Incident Desk.',
        eventTypeCode: 'OTHER',
        classificationCode: 'INTERNAL',
        unitId: null,
        venue: null,
        scheduledStart: new Date().toISOString(),
        scheduledEnd: null,
        expectedAttendance: null,
        subjectTypeCode: 'INCIDENT',
        subjectRef: incidentId,
        openMainCamera: true,
      }) as CoverageEventResponse
      setEvent(created)

      // Attach the evidence row NOW, not when the broadcast ends. A feed that went on air
      // and then dropped is still something that happened, and an evidence list that only
      // learns about clean endings is a list that quietly loses the interesting ones.
      await addEvidence(incidentId, {
        kindCode: 'LIVE_FEED',
        coverageEventId: created.id,
        caption: 'Live feed opened from the Incident Desk',
      })
      setAttached(true)
      await onAttached?.()
    } catch (err) {
      setError(explain(err))
    } finally {
      setBusy(false)
    }
  }

  const feed = event?.feeds?.[0] ?? null

  return (
    <div className="space-y-3">
      <ErrorStrip message={error} className="rounded-md border" />

      {!event && (
        <div className="space-y-2">
          <p className="text-erp-sm text-muted-foreground">
            This opens a coverage event attached to the incident and puts this device on
            air. The recording is kept by <code className="font-mono text-erp-xs">coverage</code>{' '}
            and appears in the incident's evidence list.
          </p>
          <button type="button" onClick={() => void open()} disabled={busy}
            className="erp-btn-primary h-9 px-3 inline-flex items-center gap-2 text-erp-sm">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Radio className="h-3.5 w-3.5" />}
            Open a live feed
          </button>
        </div>
      )}

      {attached && (
        <Note tone="good">
          Attached to the incident as live-feed evidence. It stays attached whether the
          broadcast ends cleanly or not.
        </Note>
      )}

      {event && feed && (
        <BroadcastPanel feedId={feed.id} feedTitle={feed.title}
          eventTitle={event.title} compact />
      )}

      {event && !feed && (
        <Note tone="bad">
          The coverage event was created but carries no camera slot. Open it in Coverage and
          add one — the evidence row is already attached, so nothing is lost.
        </Note>
      )}
    </div>
  )
}
